import React, { useState, useRef, useCallback } from 'react';
import { Send, Image as ImageIcon, Mic, Volume2, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import '@/types'; // Import type declarations

interface ChatInputProps {
  onSendMessage: (message: string, image?: string) => void;
  onUploadImage?: (file: File) => void;
  isLoading: boolean;
  placeholder?: string;
  maxLength?: number;
  allowImages?: boolean;
  allowVoice?: boolean;
  allowTTS?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onUploadImage,
  isLoading,
  placeholder = "Type your message here...",
  maxLength = 1000,
  allowImages = true,
  allowVoice = true,
  allowTTS = true,
}) => {
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragRef = useRef<HTMLFormElement>(null);
  const recognition = useRef<any>(null);

  // Initialize speech recognition
  React.useEffect(() => {
    if (allowVoice && 'webkitSpeechRecognition' in window) {
      recognition.current = new (window as any).webkitSpeechRecognition();
      recognition.current!.continuous = false;
      recognition.current!.interimResults = false;
      recognition.current!.lang = 'en-US';

      recognition.current!.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setMessage(prev => prev + transcript);
        setIsListening(false);
      };

      recognition.current!.onerror = () => {
        setIsListening(false);
      };

      recognition.current!.onend = () => {
        setIsListening(false);
      };
    }
  }, [allowVoice]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  // Drag and drop functionality
  React.useEffect(() => {
    const dragElement = dragRef.current;
    if (!dragElement || !allowImages) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Only hide if leaving the entire form area
      if (!dragElement.contains(e.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer?.files || []);
      const imageFile = files.find(file => file.type.startsWith('image/'));
      
      if (imageFile) {
        handleImageFile(imageFile);
      } else if (files.length > 0) {
        alert('Please drop an image file');
      }
    };

    dragElement.addEventListener('dragover', handleDragOver);
    dragElement.addEventListener('dragleave', handleDragLeave);
    dragElement.addEventListener('drop', handleDrop);

    return () => {
      dragElement.removeEventListener('dragover', handleDragOver);
      dragElement.removeEventListener('dragleave', handleDragLeave);
      dragElement.removeEventListener('drop', handleDrop);
    };
  }, [allowImages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() && !imagePreview) return;
    if (isLoading) return;

    onSendMessage(message.trim(), imagePreview || undefined);
    setMessage('');
    setImagePreview(null);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleImageFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (10MB limit to match FastAPI backend)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Call upload handler if provided
    onUploadImage?.(file);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    handleImageFile(file);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setImagePreview(null);
  };

  const handleVoiceInput = () => {
    if (!recognition.current || isListening) return;

    setIsListening(true);
    recognition.current.start();
  };

  const handleTTS = () => {
    if (!allowTTS || !message.trim()) return;

    const utterance = new SpeechSynthesisUtterance(message);
    utterance.rate = 0.8;
    utterance.pitch = 1;
    utterance.volume = 0.8;
    
    speechSynthesis.speak(utterance);
  };

  const canSend = (message.trim().length > 0 || imagePreview) && !isLoading;

  return (
    <form 
      ref={dragRef}
      onSubmit={handleSubmit} 
      className={clsx(
        "bg-white border-t border-gray-200 p-4 relative transition-all duration-200",
        isDragOver && allowImages && "bg-blue-50 border-blue-300"
      )}
    >
      {/* Drag overlay */}
      {isDragOver && allowImages && (
        <div className="absolute inset-0 bg-blue-100 bg-opacity-75 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center z-10">
          <div className="text-center text-blue-600">
            <ImageIcon size={48} className="mx-auto mb-2 opacity-75" />
            <p className="text-lg font-medium">Drop your image here</p>
            <p className="text-sm opacity-75">Supports JPG, PNG, GIF, WebP (max 10MB)</p>
          </div>
        </div>
      )}
      {/* Image Preview */}
      {imagePreview && (
        <div className="mb-3 relative inline-block">
          <img
            src={imagePreview}
            alt="Preview"
            className="max-w-32 max-h-32 rounded-lg border border-gray-200"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex items-end space-x-3">
        {/* Voice Input Button */}
        {allowVoice && (
          <button
            type="button"
            onClick={handleVoiceInput}
            disabled={isListening || isLoading}
            className={clsx(
              'flex-shrink-0 p-2 rounded-full transition-colors',
              isListening
                ? 'bg-red-500 text-white animate-pulse'
                : 'bg-purple-100 text-purple-600 hover:bg-purple-200',
              (isListening || isLoading) && 'opacity-50 cursor-not-allowed'
            )}
            title="Voice input"
          >
            <Mic size={20} />
          </button>
        )}

        {/* TTS Button */}
        {allowTTS && (
          <button
            type="button"
            onClick={handleTTS}
            disabled={!message.trim() || isLoading}
            className={clsx(
              'flex-shrink-0 p-2 rounded-full transition-colors',
              'bg-blue-100 text-blue-600 hover:bg-blue-200',
              (!message.trim() || isLoading) && 'opacity-50 cursor-not-allowed'
            )}
            title="Text to speech"
          >
            <Volume2 size={20} />
          </button>
        )}

        {/* Image Upload Button */}
        {allowImages && (
          <button
            type="button"
            onClick={handleImageButtonClick}
            disabled={isLoading}
            className={clsx(
              'flex-shrink-0 p-2 rounded-full transition-colors',
              'bg-gray-100 text-gray-600 hover:bg-gray-200',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
            title="Upload image"
          >
            <ImageIcon size={20} />
          </button>
        )}

        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              console.log('Textarea onChange:', e.target.value);
              setMessage(e.target.value.slice(0, maxLength));
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => console.log('Textarea focused')}
            onBlur={() => console.log('Textarea blurred')}
            onClick={() => console.log('Textarea clicked')}
            placeholder={placeholder}
            disabled={isLoading}
            rows={1}
            className={clsx(
              'w-full px-4 py-3 pr-12 border border-gray-300 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent',
              'placeholder-gray-500 text-gray-900',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
            style={{ minHeight: '48px', maxHeight: '120px' }}
            autoFocus
          />
          
          {/* Character count */}
          {message.length > maxLength * 0.8 && (
            <div className="absolute bottom-1 right-12 text-xs text-gray-400">
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!canSend}
          className={clsx(
            'flex-shrink-0 p-3 rounded-full transition-all duration-200',
            canSend
              ? 'bg-purple-600 text-white hover:bg-purple-700 hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          )}
          title="Send message"
        >
          {isLoading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Send size={20} />
          )}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />
    </form>
  );
};